/**
 * Nav Banner Web Component
 *
 * Minimal JS enhancement for nav banner.
 * Animation is handled by CSS @keyframes.
 *
 * JS only handles:
 * - Play/pause toggle (sets data-state, CSS handles animation-play-state)
 * - Live countdown timer updates (targets number spans only)
 * - Session storage for play/pause state
 */

import { Component } from "@theme/component";
import { safeSessionStorage } from "@theme/safe-storage";

/**
 * @typedef {HTMLElement & { dataset: DOMStringMap & { endDate?: string, formattedDate?: string, labelEndsOn?: string, labelEndsIn?: string } }} CountdownElement
 */

/**
 * @typedef {HTMLElement & { dataset: DOMStringMap & { endDate?: string, bannerItem?: string } }} BannerItemElement
 */

/**
 * @typedef {{
 *   playPauseButton?: HTMLButtonElement,
 *   countdown?: CountdownElement[],
 *   bannerItem?: BannerItemElement[],
 *   templateDate?: HTMLTemplateElement,
 *   templateCountdown?: HTMLTemplateElement,
 * }} NavBannerRefs
 */

/**
 * @extends {Component<NavBannerRefs>}
 */
class NavBanner extends Component {
  /** @type {number | null} */
  countdownTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.restoreState();
    this.startCountdownUpdates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopCountdownUpdates();
  }

  get state() {
    return this.dataset.state || "playing";
  }

  set state(value) {
    this.dataset.state = value;
  }

  restoreState() {
    // Only restore saved state if play/pause button exists.
    // If the button is hidden (show_play_pause = false), don't restore
    // paused state — users would have no way to resume playback.
    if (!this.refs.playPauseButton) return;

    const savedState = safeSessionStorage.getItem(
      `nav-banner-${this.dataset.blockId}`,
    );

    if (savedState) {
      this.state = savedState;
      this.updateButtonAriaLabel();
    }
  }

  updateButtonAriaLabel() {
    const button = this.refs.playPauseButton;
    if (!button) return;

    const label =
      this.state === "playing"
        ? button.dataset.labelPause
        : button.dataset.labelPlay;

    if (label) button.setAttribute("aria-label", label);
  }

  /** @param {Event} e */
  handlePlayPauseClick(e) {
    e.preventDefault();

    this.state = this.state === "playing" ? "paused" : "playing";
    this.updateButtonAriaLabel();

    safeSessionStorage.setItem(
      `nav-banner-${this.dataset.blockId}`,
      this.state,
    );
  }

  startCountdownUpdates() {
    const countdowns = this.refs.countdown;
    const bannerItems = this.refs.bannerItem;

    const hasCountdowns =
      countdowns && (!Array.isArray(countdowns) || countdowns.length > 0);
    const itemsArray = bannerItems
      ? Array.isArray(bannerItems)
        ? bannerItems
        : [bannerItems]
      : [];
    const hasItemsWithExpiry = itemsArray.some((item) => item.dataset.endDate);

    // Start timer if there are countdown displays OR banner items with expiry dates
    if (!hasCountdowns && !hasItemsWithExpiry) return;

    this.countdownTimer = setInterval(() => {
      this.updateCountdowns();
      this.checkBannerItemExpiry();
    }, 1000);
  }

  stopCountdownUpdates() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  updateCountdowns() {
    const countdowns = this.refs.countdown;
    if (!countdowns) return;

    const items = Array.isArray(countdowns) ? countdowns : [countdowns];

    for (const container of items) {
      const endDateStr = container.dataset.endDate;
      if (!endDateStr) continue;

      const endDate = new Date(parseInt(endDateStr, 10) * 1000);
      const now = new Date();
      const timeRemaining = endDate.getTime() - now.getTime();

      if (timeRemaining <= 0) {
        container.hidden = true;
        continue;
      }

      const totalSeconds = Math.floor(timeRemaining / 1000);
      const totalMinutes = Math.floor(totalSeconds / 60);
      const totalHours = Math.floor(totalMinutes / 60);

      const needsDateFormat = totalHours >= 48;
      const hasTimeSpans = container.querySelector('[ref="hours"]');
      const isCurrentlyDateFormat = !hasTimeSpans;

      if (needsDateFormat && !isCurrentlyDateFormat) {
        this.rebuildAsDateFormat(container, endDate);
      } else if (!needsDateFormat && isCurrentlyDateFormat) {
        this.rebuildAsCountdownFormat(
          container,
          totalHours,
          totalMinutes % 60,
          totalSeconds % 60,
        );
      } else if (needsDateFormat) {
        // Use server-formatted date to avoid timezone shifts
        const dateStr =
          container.dataset.formattedDate || this.formatDate(endDate);
        this.updateText(container, "date", dateStr);
      } else {
        this.updateText(container, "hours", this.pad(totalHours));
        this.updateText(container, "minutes", this.pad(totalMinutes % 60));
        this.updateText(container, "seconds", this.pad(totalSeconds % 60));
      }
    }

    // Note: Stop logic is handled by checkBannerItemExpiry() which runs after this
  }

  /**
   * Check banner items for expiry and hide expired ones.
   * Called every second alongside updateCountdowns.
   * Handles expiry even when countdown display is hidden (hide_countdown setting).
   */
  checkBannerItemExpiry() {
    const bannerItems = this.refs.bannerItem;
    if (!bannerItems) {
      // No banner items with expiry tracking - stop the timer if no countdowns either
      if (!this.refs.countdown) {
        this.stopCountdownUpdates();
      }
      return;
    }

    const items = Array.isArray(bannerItems) ? bannerItems : [bannerItems];
    const now = Date.now();
    let visibleCount = 0;
    let itemsWithExpiryCount = 0;
    let expiredCount = 0;
    let primaryExpired = false;

    for (const item of items) {
      const isPrimary = item.dataset.bannerItem === "1";
      const endDateStr = item.dataset.endDate;

      // Track items that have expiry dates (for timer stop logic)
      if (endDateStr) {
        itemsWithExpiryCount++;
      }

      // Skip already hidden items
      if (item.hidden) {
        if (isPrimary) primaryExpired = true;
        if (endDateStr) expiredCount++;
        continue;
      }

      if (!endDateStr) {
        // No expiry date - item stays visible
        visibleCount++;
        continue;
      }

      const endDate = parseInt(endDateStr, 10) * 1000;
      if (now >= endDate) {
        // Expired - hide the entire banner item
        item.hidden = true;
        expiredCount++;
        if (isPrimary) primaryExpired = true;
      } else {
        visibleCount++;
      }
    }

    // If primary banner expired, hide the entire banner.
    // Liquid only renders when primary exists, so secondary alone is inconsistent.
    if (primaryExpired) {
      this.hidden = true;
      this.stopCountdownUpdates();
      return;
    }

    // When only one item remains visible out of multiple, disable rotation animation.
    // CSS :only-child doesn't match when hidden siblings exist, so we use a data attribute.
    if (items.length > 1 && visibleCount === 1) {
      this.dataset.singleVisible = "";
    }

    // When all items have expired, hide the entire banner and stop tracking
    if (visibleCount === 0) {
      this.hidden = true;
      this.stopCountdownUpdates();
      return;
    }

    // Stop the timer when all items with expiry dates have expired.
    // No point continuing to check if only non-expiring items remain.
    if (itemsWithExpiryCount > 0 && expiredCount === itemsWithExpiryCount) {
      this.stopCountdownUpdates();
    }
  }

  /**
   * @param {CountdownElement} container
   * @param {Date} endDate
   */
  rebuildAsDateFormat(container, endDate) {
    if (!this.refs.templateDate) return;

    const { fragment, refs } = this.cloneTemplateWithRefs(
      this.refs.templateDate,
    );
    // Use server-formatted date to avoid timezone shifts
    const dateStr = container.dataset.formattedDate || this.formatDate(endDate);
    if (refs.label) refs.label.textContent = container.dataset.labelEndsOn;
    if (refs.date) refs.date.textContent = dateStr;
    container.replaceChildren(fragment);
  }

  /**
   * @param {CountdownElement} container
   * @param {number} hours
   * @param {number} minutes
   * @param {number} seconds
   */
  rebuildAsCountdownFormat(container, hours, minutes, seconds) {
    if (!this.refs.templateCountdown) return;

    const { fragment, refs } = this.cloneTemplateWithRefs(
      this.refs.templateCountdown,
    );
    if (refs.label) refs.label.textContent = container.dataset.labelEndsIn;
    if (refs.hours) refs.hours.textContent = this.pad(hours);
    if (refs.minutes) refs.minutes.textContent = this.pad(minutes);
    if (refs.seconds) refs.seconds.textContent = this.pad(seconds);
    container.replaceChildren(fragment);
  }

  /**
   * Clone a template and collect refs from the cloned content.
   * @param {HTMLTemplateElement} template
   * @returns {{ fragment: DocumentFragment, refs: Record<string, HTMLElement> }}
   */
  cloneTemplateWithRefs(template) {
    /** @type {DocumentFragment} */
    const fragment = /** @type {DocumentFragment} */ (
      template.content.cloneNode(true)
    );
    /** @type {Record<string, HTMLElement>} */
    const refs = {};

    for (const el of fragment.querySelectorAll("[ref]")) {
      const refName = el.getAttribute("ref");
      if (refName) {
        refs[refName] = /** @type {HTMLElement} */ (el);
      }
    }

    return { fragment, refs };
  }

  /**
   * @param {CountdownElement} container
   * @param {string} refName
   * @param {string} value
   */
  updateText(container, refName, value) {
    const el = container.querySelector(`[ref="${refName}"]`);
    if (!el) return;

    const stringValue = String(value);
    if (el.textContent !== stringValue) {
      el.textContent = stringValue;
    }
  }

  /**
   * @param {number} num
   * @returns {string}
   */
  pad(num) {
    return String(num).padStart(2, "0");
  }

  /**
   * @param {Date} date
   * @returns {string}
   */
  formatDate(date) {
    const locale = document.documentElement.lang || navigator.language || "en";
    return date.toLocaleString(locale, { month: "long", day: "numeric" });
  }
}

if (!customElements.get("nav-banner")) {
  customElements.define("nav-banner", NavBanner);
}
